import type {
  IWidgetThemeConfig,
  IPreviewMessage,
  IPreviewModeConfig,
  IWidgetSettings,
  IWidgetSettingsUpdate,
} from '../types';

/**
 * Check if widget is running in preview mode.
 * Preview mode is activated via ?preview=true query parameter.
 */
export function detectPreviewMode(): IPreviewModeConfig {
  try {
    // Check for global WIDGET_PREVIEW flag (set via srcDoc in admin preview)
    const globalPreview = (window as { WIDGET_PREVIEW?: boolean }).WIDGET_PREVIEW === true;

    // Also check URL params (for direct preview URL)
    const params = new URLSearchParams(window.location.search);
    const urlPreview = params.get('preview') === 'true';

    const isPreviewMode = globalPreview || urlPreview;
    const parentOrigin = params.get('parentOrigin') || undefined;

    return {
      isPreviewMode,
      parentOrigin,
    };
  } catch {
    return { isPreviewMode: false };
  }
}

/**
 * Initialize preview mode listeners for postMessage communication.
 */
export function initPreviewListeners(
  onThemeUpdate: (config: Partial<IWidgetThemeConfig>) => void,
  onStateToggle: (state: 'open' | 'closed') => void,
  onSettingsUpdate: (settings: IWidgetSettingsUpdate) => void,
  parentOrigin?: string
): () => void {
  const handleMessage = (event: MessageEvent) => {
    // Validate origin if specified
    if (parentOrigin && event.origin !== parentOrigin) {
      return;
    }

    const data = event.data as IPreviewMessage;
    if (!data || typeof data !== 'object' || !data.type) {
      return;
    }

    switch (data.type) {
      case 'theme_update':
        if (data.payload && data.payload.config) {
          onThemeUpdate(data.payload.config);
        }
        break;

      case 'toggle_state':
        if (data.payload && data.payload.state) {
          onStateToggle(data.payload.state);
        }
        break;

      case 'settings_update':
        if (data.payload && data.payload.settings) {
          onSettingsUpdate(data.payload.settings);
        }
        break;
    }
  };

  window.addEventListener('message', handleMessage);

  // Notify parent that preview is ready
  try {
    window.parent.postMessage({ type: 'preview_ready' }, '*');
  } catch {
    // Silent failure
  }

  // Return cleanup function
  return () => {
    window.removeEventListener('message', handleMessage);
  };
}

/**
 * Default theme configuration for preview mode.
 */
export const PREVIEW_THEME_DEFAULTS: IWidgetThemeConfig = {
  'primary-color': '#2563eb',
  'primary-hover': '#1d4ed8',
  'bg-color': '#ffffff',
  'header-bg': '#f8fafc',
  'text-color': '#1e293b',
  'text-secondary': '#64748b',
  'border-color': '#e2e8f0',
  'user-message-bg': '#2563eb',
  'user-message-text': '#ffffff',
  'bot-message-bg': '#f1f5f9',
  'bot-message-text': '#1e293b',
  'font-family': 'system-ui, -apple-system, sans-serif',
  'font-size-base': '14px',
  'font-size-small': '12px',
  'border-radius': '8px',
  'border-radius-small': '4px',
  'message-radius': '12px',
  shadow: '0 1px 3px rgba(0,0,0,0.1)',
  'shadow-lg': '0 10px 25px rgba(0,0,0,0.15)',
};

/**
 * Default suggested questions for preview mode.
 */
export const PREVIEW_SUGGESTED_QUESTIONS = [
  'What does your product do?',
  'How do I get started?',
  'What are the pricing plans?',
];

/**
 * Default widget settings for preview mode.
 * Matches backend defaults in GetWidgetConfigUseCase.
 */
export const PREVIEW_SETTINGS_DEFAULTS: IWidgetSettings = {
  headerText: 'Chat with us',
  welcomeTeaser: {
    enabled: true,
    text: 'Hi! Need help? Ask me anything!',
    delaySeconds: 3,
  },
  welcomeMessage: "Hi! I'm here to help you. What would you like to know?",
  suggestedQuestions: {
    enabled: true,
    items: PREVIEW_SUGGESTED_QUESTIONS,
    displayCount: 3,
  },
  allowGeneralKnowledge: false,
  inputPlaceholder: 'Type a message...',
  fallback: {
    unansweredMessage: "I don't have enough information to answer that.",
    postEmailMessage: 'Thanks! The team will get back to you soon.',
  },
};

/**
 * Static mock messages for preview mode (used as fallback).
 */
export const PREVIEW_MESSAGES = [
  {
    id: 'preview-1',
    type: 'user' as const,
    text: 'How do I get started with your platform?',
  },
  {
    id: 'preview-2',
    type: 'bot' as const,
    text: 'Welcome! Getting started is easy. First, create your account, then follow our quick setup wizard. Would you like me to guide you through the process?',
    sources: [
      { pageUrl: 'https://example.com/docs/getting-started', title: 'Getting Started Guide' },
      { pageUrl: 'https://example.com/docs/quick-start', title: 'Quick Start Tutorial' },
    ],
  },
];

/**
 * Create preview messages based on current settings.
 * Shows: welcome message → user question → bot answer → unanswered message → post-email message
 */
export function createPreviewMessages(settings: IWidgetSettings): Array<{
  id: string;
  type: 'user' | 'bot';
  text: string;
  sources?: Array<{ pageUrl: string; title?: string }>;
}> {
  const welcomeMessage = settings.welcomeMessage ?? PREVIEW_SETTINGS_DEFAULTS.welcomeMessage;
  const unansweredMessage = settings.fallback?.unansweredMessage ?? PREVIEW_SETTINGS_DEFAULTS.fallback.unansweredMessage;
  const postEmailMessage = settings.fallback?.postEmailMessage ?? PREVIEW_SETTINGS_DEFAULTS.fallback.postEmailMessage;

  return [
    // Welcome message (first thing user sees when opening chat)
    {
      id: 'preview-welcome',
      type: 'bot' as const,
      text: welcomeMessage,
    },
    // User asks a question
    {
      id: 'preview-user-1',
      type: 'user' as const,
      text: 'How do I get started?',
    },
    // Bot answers successfully (normal flow)
    {
      id: 'preview-bot-1',
      type: 'bot' as const,
      text: 'Getting started is easy! First, create your account, then follow our quick setup wizard.',
      sources: [
        { pageUrl: 'https://example.com/docs/getting-started', title: 'Getting Started Guide' },
      ],
    },
    // User asks something that can't be answered
    {
      id: 'preview-user-2',
      type: 'user' as const,
      text: 'What are your office hours?',
    },
    // Unanswered message (example of what shows when AI can't answer)
    {
      id: 'preview-unanswered',
      type: 'bot' as const,
      text: unansweredMessage,
    },
    // Post-email message (example of what shows after user submits email)
    {
      id: 'preview-post-email',
      type: 'bot' as const,
      text: postEmailMessage,
    },
  ];
}
